import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import Hoja from './Hoja'
import Portada from './Portada'
import {
  candidatosTomo,
  fijarTomo,
  listaTomos,
  soltarTomo,
  tomosGuardados,
  type Candidato,
} from '../api/tomos'
import { borrarPortada, guardarPortada } from '../lib/portadas'

interface Props {
  mangaId: number
  ruta: string | null
  titulo: string
}

/**
 * La portada de la ficha, que se puede cambiar tocandola.
 *
 * Tres portadas posibles, y mandan en este orden: la **puesta a mano** —para
 * cuando la fuente trae una fea, con marca de agua o rota—, la del **ultimo
 * tomo** que encuentra el servicio en MangaDex, y la de la fuente.
 *
 * Aqui tambien se corrige el emparejamiento con MangaDex, que se hace solo y
 * puede equivocarse: buscando el titulo salen spin-offs y ediciones a color
 * con nombres casi iguales.
 */
export default function CambiarPortada({ mangaId, ruta, titulo }: Props) {
  const clienteConsultas = useQueryClient()
  const entrada = useRef<HTMLInputElement>(null)
  const [abierto, setAbierto] = useState(false)
  const [eligiendo, setEligiendo] = useState(false)
  const [fallo, setFallo] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const { data: tomos } = useQuery({
    queryKey: ['tomos'],
    queryFn: listaTomos,
    initialData: tomosGuardados,
    // Lo guardado se pinta ya, pero cuenta como viejo: sin esto React Query
    // lo daria por recien traido y no preguntaria al servidor hasta dentro de
    // diez minutos, asi que la primera vez —cache vacio— la biblioteca se
    // quedaba sin portadas de tomo hasta recargar.
    initialDataUpdatedAt: 0,
    staleTime: 10 * 60 * 1000,
  })
  const tomo = tomos?.[mangaId]

  const { data: candidatas, isLoading: buscandoCandidatas } = useQuery({
    queryKey: ['tomosCandidatos', mangaId, titulo],
    queryFn: () => candidatosTomo(mangaId, titulo),
    enabled: eligiendo,
    staleTime: 10 * 60 * 1000,
  })

  async function refrescar() {
    await clienteConsultas.invalidateQueries({ queryKey: ['portadaPropia', mangaId] })
  }

  async function refrescarTomos() {
    await clienteConsultas.invalidateQueries({ queryKey: ['tomos'] })
  }

  async function alElegir(evento: React.ChangeEvent<HTMLInputElement>) {
    const archivo = evento.target.files?.[0]
    // Se limpia siempre: si no, volver a elegir la misma foto no dispara el
    // evento y parece que la app se ha quedado colgada.
    evento.target.value = ''
    if (!archivo) return

    setFallo(null)
    try {
      await guardarPortada(mangaId, archivo)
      await refrescar()
      setAbierto(false)
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se pudo guardar la portada')
    }
  }

  async function quitar() {
    await borrarPortada(mangaId)
    await refrescar()
    setAbierto(false)
  }

  /** Corre algo del servicio de tomos y deja el fallo a la vista si lo hay. */
  async function conElServicio(trabajo: () => Promise<unknown>, cerrar = true) {
    setFallo(null)
    setOcupado(true)
    try {
      await trabajo()
      await refrescarTomos()
      if (cerrar) {
        setEligiendo(false)
        setAbierto(false)
      }
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se pudo hablar con el servidor')
    } finally {
      setOcupado(false)
    }
  }

  function bloqueTomo() {
    if (eligiendo) {
      return (
        <>
          <p className="meta">
            La serie en MangaDex, que es de donde salen las portadas de los tomos. Si la
            automatica no es la buena, elige la correcta.
          </p>
          {buscandoCandidatas && <p className="meta">Buscando...</p>}
          {candidatas?.length === 0 && <p className="meta">MangaDex no devuelve nada.</p>}
          <ul className="lista candidatos-tomo">
            {candidatas?.map((c: Candidato) => (
              <li key={c.uuid}>
                <button
                  className="candidato"
                  disabled={ocupado}
                  onClick={() => conElServicio(() => fijarTomo(mangaId, c.uuid, titulo))}
                >
                  {c.miniatura ? (
                    <img src={c.miniatura} alt="" loading="lazy" />
                  ) : (
                    <span className="candidato-hueco" />
                  )}
                  <span className="candidato-datos">
                    <span className="candidato-titulo">{c.titulo}</span>
                    <span className="meta">
                      {[c.ano, c.idioma === 'ja' ? 'japones' : c.idioma]
                        .filter(Boolean)
                        .join(' · ')}
                      {c.uuid === tomo?.uuid ? ' · la de ahora' : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button className="boton secundario" onClick={() => setEligiendo(false)}>
            Volver
          </button>
        </>
      )
    }

    if (tomo?.estado === 'lista') {
      return (
        <>
          <p className="meta">
            Ahora se ve la portada del <strong>tomo {tomo.volumen}</strong>, de{' '}
            {tomo.titulo} en MangaDex{tomo.fijado ? ' (elegida por ti)' : ''}. Cambia sola
            cuando sale un tomo nuevo.
          </p>
          <button className="boton secundario" onClick={() => setEligiendo(true)}>
            No es esa serie
          </button>
          <button
            className="boton secundario"
            disabled={ocupado}
            onClick={() => conElServicio(() => fijarTomo(mangaId, null, titulo))}
          >
            No usar el tomo aqui
          </button>
        </>
      )
    }

    if (tomo?.estado === 'apagado') {
      return (
        <>
          <p className="meta">Aqui no se usa la portada del tomo.</p>
          <button
            className="boton secundario"
            disabled={ocupado}
            onClick={() => conElServicio(() => soltarTomo(mangaId, titulo))}
          >
            Volver a buscarla
          </button>
        </>
      )
    }

    // 'sin' y lo que no se ha mirado todavia: casi siempre es que no es manga
    return (
      <>
        <p className="meta">
          No hay portada de tomo: el servidor no encontro esta serie en MangaDex, o no es
          manga japones.
        </p>
        <button className="boton secundario" onClick={() => setEligiendo(true)}>
          Buscarla a mano
        </button>
      </>
    )
  }

  return (
    <>
      <button
        className="portada-tocable"
        onClick={() => setAbierto(true)}
        title="Cambiar la portada"
      >
        <Portada mangaId={mangaId} ruta={ruta} titulo={titulo} />
        <span className="portada-lapiz" aria-hidden="true">
          ✎
        </span>
      </button>

      <input
        ref={entrada}
        type="file"
        accept="image/*"
        hidden
        onChange={alElegir}
      />

      {abierto && (
        <Hoja
          titulo="Portada"
          nota="La imagen que elijas se guarda en este telefono y sustituye a la de la fuente."
          alCerrar={() => {
            setEligiendo(false)
            setAbierto(false)
          }}
        >
          {fallo && <p className="error-texto">{fallo}</p>}

          {!eligiendo && (
            <>
              <button className="boton" onClick={() => entrada.current?.click()}>
                Elegir una imagen
              </button>
              <button className="boton secundario" onClick={quitar}>
                Volver a la de la fuente
              </button>
            </>
          )}

          <div className="bloque-tomo">
            <p className="titulo-seccion">Portada del ultimo tomo</p>
            {bloqueTomo()}
          </div>

          {!eligiendo && (
            <button className="boton secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </button>
          )}
        </Hoja>
      )}
    </>
  )
}
